/**
 * Application-status enums + per-status display metadata. Lifted from
 * customer-app/src/pages/applicants/helpers.ts in Phase 7-dashboard-b so the
 * candidate dashboard (My Job Applications) can render identical badges.
 */

export type ApplicationStatus =
  | 'APPLIED'
  | 'UNDER_REVIEW'
  | 'SHORTLISTED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'HIRED';

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  APPLIED: 'Applied',
  UNDER_REVIEW: 'Under review',
  SHORTLISTED: 'Shortlisted',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
  HIRED: 'Hired',
};

export const APPLICATION_STATUS_TONE: Record<
  ApplicationStatus,
  'sky' | 'amber' | 'green' | 'red' | 'gray' | 'emerald'
> = {
  APPLIED: 'sky',
  UNDER_REVIEW: 'amber',
  SHORTLISTED: 'green',
  REJECTED: 'red',
  WITHDRAWN: 'gray',
  HIRED: 'emerald',
};

/**
 * Recruitment workflow §5.5 — the status a CANDIDATE sees on the website: the client status, or
 * UNDER_PROCESSING while the application is in QuantumHire's internal funnel (internal
 * rejections show as REJECTED). Deliberately a separate type from `ApplicationStatus`, which also
 * drives the customer app's funnel maps and transitions — UNDER_PROCESSING must never appear
 * there. Website only.
 */
export type CandidateApplicationStatus = ApplicationStatus | 'UNDER_PROCESSING';

export const CANDIDATE_APPLICATION_STATUS_LABELS: Record<CandidateApplicationStatus, string> = {
  ...APPLICATION_STATUS_LABELS,
  UNDER_PROCESSING: 'Under Processing',
};

export const CANDIDATE_APPLICATION_STATUS_TONE: Record<
  CandidateApplicationStatus,
  'sky' | 'amber' | 'green' | 'red' | 'gray' | 'emerald'
> = {
  ...APPLICATION_STATUS_TONE,
  UNDER_PROCESSING: 'amber',
};

/**
 * Server-enforced state machine — mirror of `ALLOWED_STATUS_TRANSITIONS` in
 * `backend/src/modules/company/company-applications.dto.ts`. Companies cannot
 * write WITHDRAWN (candidate-initiated only); company-initiated removal uses
 * REJECTED with a comment per Phase 9.0 §7.8.
 */
export const ALLOWED_STATUS_TRANSITIONS: Record<ApplicationStatus, ApplicationStatus[]> = {
  APPLIED: ['UNDER_REVIEW', 'SHORTLISTED', 'REJECTED'],
  UNDER_REVIEW: ['SHORTLISTED', 'REJECTED', 'APPLIED'],
  SHORTLISTED: ['HIRED', 'REJECTED', 'UNDER_REVIEW'],
  REJECTED: ['APPLIED', 'UNDER_REVIEW'],
  HIRED: [],
  WITHDRAWN: [],
};

export const FUNNEL_TABS: ApplicationStatus[] = [
  'APPLIED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'REJECTED',
  'WITHDRAWN',
  'HIRED',
];

export function matchScoreTone(score: number): 'gray' | 'amber' | 'green' | 'emerald' | 'red' {
  if (score >= 86) return 'emerald';
  if (score >= 71) return 'green';
  if (score >= 56) return 'amber';
  if (score >= 31) return 'gray';
  return 'red';
}
